import type { GbTier } from "@/lib/pricing-utils";

export const PRICING_VERIFIED_DATE = "July 9, 2026";

export const BRIGHTDATA_URL = "https://brightdata.com";
export const BRIGHTDATA_PROXY_TYPES_URL = "https://brightdata.com/proxy-types";
export const BRIGHTDATA_RESIDENTIAL_URL = "https://brightdata.com/proxy-types/residential-proxies";
export const BRIGHTDATA_ISP_URL = "https://brightdata.com/proxy-types/isp-proxies";
export const BRIGHTDATA_DATACENTER_URL = "https://brightdata.com/proxy-types/datacenter-proxies";
export const BRIGHTDATA_AUP_URL = "https://brightdata.com/acceptable-use-policy";
export const BRIGHTDATA_TRUSTPILOT_URL = "https://www.trustpilot.com/review/brightdata.com";
export const BRIGHTDATA_G2_URL = "https://www.g2.com/products/bright-data/reviews";

export type ProxyProduct = "mobile" | "residential";

export interface BrightDataPlan {
  name: string;
  gb: number;
  monthlyPrice: number;
  perGb: number;
}

// Bright Data's current public proxy pages (brightdata.com/proxy-types) list only
// Residential, ISP, and Datacenter — no mobile proxy category. These are the
// residential committed-volume tiers; below the smallest tier, buyers pay the
// pay-as-you-go rate instead.
export const BRIGHTDATA_RESIDENTIAL_PLANS: BrightDataPlan[] = [
  { name: "141 GB plan", gb: 141, monthlyPrice: 499, perGb: 3.5 },
  { name: "332 GB plan", gb: 332, monthlyPrice: 999, perGb: 3.0 },
  { name: "798 GB plan", gb: 798, monthlyPrice: 1999, perGb: 2.5 },
];

export const BRIGHTDATA_RESIDENTIAL_PAYG_PER_GB = 4.0;

export const PROXIDIZE_MOBILE_FALLBACK_TIERS: GbTier[] = [
  { gb: 25, monthlyPPG: 2, totalPrice: 50 },
  { gb: 50, monthlyPPG: 2, totalPrice: 100 },
  { gb: 100, monthlyPPG: 2, totalPrice: 200 },
  { gb: 200, monthlyPPG: 2, totalPrice: 400 },
  { gb: 500, monthlyPPG: 2, totalPrice: 1000 },
  { gb: 1000, monthlyPPG: 2, totalPrice: 2000 },
  { gb: 2000, monthlyPPG: 2, totalPrice: 4000 },
  { gb: 5000, monthlyPPG: 2, totalPrice: 10000 },
  { gb: 10000, monthlyPPG: 2, totalPrice: 20000 },
];

export const PROXIDIZE_RESIDENTIAL_FALLBACK_TIERS: GbTier[] = [
  { gb: 25, monthlyPPG: 1, totalPrice: 25 },
  { gb: 50, monthlyPPG: 1, totalPrice: 50 },
  { gb: 100, monthlyPPG: 1, totalPrice: 100 },
  { gb: 200, monthlyPPG: 1, totalPrice: 200 },
  { gb: 500, monthlyPPG: 1, totalPrice: 500 },
  { gb: 1000, monthlyPPG: 1, totalPrice: 1000 },
  { gb: 2000, monthlyPPG: 0.95, totalPrice: 1900 },
  { gb: 5000, monthlyPPG: 0.95, totalPrice: 4750 },
  { gb: 10000, monthlyPPG: 0.9, totalPrice: 9000 },
];

export interface BrightDataCost {
  cost: number;
  planLabel: string;
  extrapolated: boolean;
}

// Bright Data has no public mobile proxy pricing to compare against, so this
// only models residential: pay-as-you-go at $4.00/GB, or a committed tier
// when that tier's flat price beats the pay-as-you-go rate.
export function brightDataResidentialCost(gb: number): BrightDataCost {
  const payAsYouGo = Math.round(gb * BRIGHTDATA_RESIDENTIAL_PAYG_PER_GB);
  const plan = BRIGHTDATA_RESIDENTIAL_PLANS.find((p) => p.gb >= gb);

  if (plan && plan.monthlyPrice < payAsYouGo) {
    return {
      cost: plan.monthlyPrice,
      planLabel: `${plan.name} (${plan.gb} GB)`,
      extrapolated: false,
    };
  }

  if (!plan) {
    const top = BRIGHTDATA_RESIDENTIAL_PLANS[BRIGHTDATA_RESIDENTIAL_PLANS.length - 1];
    const overage = Math.round(top.monthlyPrice + (gb - top.gb) * top.perGb);
    if (overage < payAsYouGo) {
      return { cost: overage, planLabel: `${top.name} + top-up`, extrapolated: true };
    }
  }

  return {
    cost: payAsYouGo,
    planLabel: "Pay As You Go ($4.00/GB)",
    extrapolated: false,
  };
}
